import React from 'react';
import { FaLock, FaChartLine, FaFileAlt, FaBullhorn, FaUserCheck, FaArrowRight, FaShieldAlt, FaChartBar, FaStar, FaRocket, FaLightbulb, FaCrown, FaAward, FaCheckCircle, FaGlobe, FaMobileAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import bull from '../../assets/images/bull.png';
import  aboutIcons  from '../../assets/icons/13892616.png';
import  aboutIcons2  from '../../assets/icons/11242222.png';

const AboutPortal = ({ contentData }) => {
  const features = [
    {
      icon: <FaChartLine className="text-xl" />,
      title: contentData?.pmp_right_card1_title,
      desc: contentData?.pmp_right_card1_info,
      color: "from-blue-500 to-blue-400",
      delay: 0.1
    },
    {
      icon: <FaFileAlt className="text-xl" />,
      title: contentData?.pmp_right_card2_title,
      desc: contentData?.pmp_right_card2_info,
      color: "from-emerald-500 to-emerald-400",
      delay: 0.2
    },
    {
      icon: <FaBullhorn className="text-xl" />,
      title: contentData?.pmp_right_card3_title,
      desc: contentData?.pmp_right_card3_info,
      color: "from-violet-500 to-violet-400",
      delay: 0.3
    },
    {
      icon: <FaUserCheck className="text-xl" />,
      title: contentData?.pmp_right_card4_title,
      desc: contentData?.pmp_right_card4_info,
      color: "from-amber-500 to-amber-400",
      delay: 0.4
    }
  ];

  const highlights = [
    { icon: <FaShieldAlt />, text: contentData?.pmp_card1_text, color: "text-blue-600" },
    { icon: <FaCheckCircle />, text: contentData?.pmp_card2_text, color: "text-emerald-600" },
    { icon: <FaGlobe />, text: contentData?.pmp_card3_text, color: "text-violet-600" },
    { icon: <FaMobileAlt />, text: contentData?.pmp_card4_text, color: "text-amber-600" }
  ];

  return (
    <section className="relative py-20 bg-white overflow-hidden">
      {/* Background - Subtle & Modern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Your Animated Images - Subtle Animation */}
        <motion.div
          animate={{ 
            x: [0, 20, 0],
            y: [0, -15, 0]
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
          className="absolute top-20 left-0 opacity-10"
        >
          <img 
            src={bull} 
            alt="Chart" 
            className="transform -scale-x-100 w-96"
            height={96}
            width={96}
            
          />
        </motion.div>
        
        <motion.div
          animate={{ 
            x: [0, -15, 0],
            y: [0, 10, 0]
          }}
          transition={{ 
            duration: 9, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
          className="absolute top-32 right-20 opacity-10"
        >
          <img 
            src={aboutIcons} 
            alt="Finance Chart" 
            className="w-80"
            height={80}
            width={80}
          />
        </motion.div>
        
        <motion.div
          animate={{ 
            x: [0, -10, 0],
            y: [0, 8, 0]
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 4
          }}
          className="absolute bottom-20 right-40 opacity-10"
        >
          <img 
            src= {aboutIcons2} 
            alt="Stock Chart" 
            className="w-72"
            height={72}
            width={72}
          />
        </motion.div>
      </div>

      {/* Abstract Background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Geometric Grid */}
        <div className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, #000 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
        
        {/* Floating Gradient Shapes */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            animate={{
              x: [0, Math.sin(i) * 50, 0],
              y: [0, Math.cos(i) * 50, 0],
              rotate: [0, 180, 360]
            }}
            transition={{
              duration: 20 + i * 3,
              repeat: Infinity,
              ease: "linear"
            }}
            className={`absolute opacity-5 ${
              i % 3 === 0 ? 'bg-gradient-to-r from-blue-200 to-cyan-200' : 
              i % 3 === 1 ? 'bg-gradient-to-r from-emerald-200 to-green-200' : 
              'bg-gradient-to-r from-violet-200 to-purple-200'
            }`}
            style={{
              width: `${200 + i * 60}px`,
              height: `${200 + i * 60}px`,
              left: `${(i * 15) % 100}%`,
              top: `${(i * 20) % 100}%`,
              borderRadius: i % 2 === 0 ? '30% 70% 70% 30% / 30% 30% 70% 70%' : '50%'
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-full mb-6 border border-blue-100"
          >
            <FaCrown className="text-amber-500" />
            <span className="text-blue-700 font-semibold text-sm tracking-wider">
              PREMIUM MEMBER PORTAL
            </span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
          >
            Your Exclusive
              Research Dashboard
            
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-gray-600 text-lg max-w-2xl mx-auto"
          >

            Welcome to your premium research portal - designed exclusively for Market Maestroo members
          </motion.p>
        </div>

     <motion.div
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.4, staggerChildren: 0.1 }}
  viewport={{ once: true }}
  className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16"
>
  {highlights.map((highlight, index) => {
    // Define background colors for each card
    const bgColors = [
      "bg-blue-500",
      "bg-emerald-500", 
      "bg-violet-500",
      "bg-amber-500"
    ];
    
    const borderColors = [
      "border-blue-500",
      "border-emerald-500",
      "border-violet-500",
      "border-amber-500"
    ];
    
    const iconColors = [
      "text-blue-100",
      "text-emerald-100",
      "text-violet-100", 
      "text-amber-100"
    ];

    return (
      <motion.div
        key={index}
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: index * 0.1 }}
        whileHover={{ 
          y: -4,
          scale: 1.02,
          transition: { duration: 0.15 }
        }}
        className="group relative cursor-pointer"
      >
        {/* Main Card with Colored Background */}
        <div className={`relative ${bgColors[index]} rounded-2xl p-6 text-center transition-all duration-300 group-hover:bg-white`}>
          {/* Thick Border on Hover */}
          <div className={`absolute inset-0 rounded-2xl border-4 ${borderColors[index]} opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none`}></div>
          
          {/* Icon Container */}
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-4 transition-all duration-300 group-hover:bg-gray-50 group-hover:scale-110">
            <div className={`text-2xl ${iconColors[index]} transition-colors duration-300 group-hover:${borderColors[index].replace('border-', 'text-')}`}>
              {highlight.icon}
            </div>
          </div>
          
          {/* Text */}
          <div className={`font-bold text-white text-base mb-1 transition-colors duration-300 group-hover:text-gray-900`}>
            {highlight.text}
          </div>
        </div>
      </motion.div>
    );
  })}
</motion.div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Main Card */}
            <div className="bg-gradient-to-br from-white to-blue-50/50 rounded-2xl p-8 border border-blue-100 shadow-sm hover:shadow-lg transition-all duration-300 relative overflow-hidden group">
              {/* Glow Effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-100 to-transparent rounded-2xl opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
              
              <div className="flex items-center gap-4 mb-6 relative">
                <div className="w-14 h-14 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <FaLock className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{contentData?.pmp_left_card_title}</h3>
                  <p className="text-blue-600 text-sm">{contentData?.pmp_left_card_desc}</p>
                </div>
              </div>
              
              <p className="text-gray-700 mb-8 leading-relaxed">
                {contentData?.pmp_left_card_info}
              </p>
              
              <div className="space-y-4">
                {[
                  contentData?.pmp_left_card_text1,
                  contentData?.pmp_left_card_text2,
                  contentData?.pmp_left_card_text3,
                  contentData?.pmp_left_card_text4,
                  contentData?.pmp_left_card_text5
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 group/item">
                    <div className="w-6 h-6 bg-gradient-to-r from-emerald-100 to-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    </div>
                    <span className="text-gray-700 group-hover/item:text-gray-900 transition-colors">
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Note Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-400 rounded-xl p-5"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FaLightbulb className="text-amber-600" />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Important Note</h4>
                  <p className="text-amber-800 text-sm">
                    {contentData?.pmp_importent_note_text}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Features */}
          <div className="space-y-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: feature.delay }}
                viewport={{ once: true }}
                className="group"
              >
                <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 relative overflow-hidden">
                  {/* Hover Gradient Background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                  
                  <div className="flex items-start gap-5 relative">
                    {/* Icon */}
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className={`w-16 h-16 bg-gradient-to-r ${feature.color} rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0`}
                    >
                      <div className="text-white text-2xl">
                        {feature.icon}
                      </div>
                    </motion.div>
                    
                    {/* Content */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-lg font-bold text-gray-900">{feature.title}</h4>
                        <motion.div
                          initial={{ opacity: 0, x: -10 }}
                          whileHover={{ opacity: 1, x: 0 }}
                          className="text-blue-500"
                        >
                          <FaArrowRight className="text-sm" />
                        </motion.div>
                      </div>
                      <p className="text-gray-600 mb-4">{feature.desc}</p>
                      
                      {/* Progress Indicator */}
                      <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          whileInView={{ width: "100%" }}
                          transition={{ duration: 1, delay: feature.delay + 0.3 }}
                          viewport={{ once: true }}
                          className={`h-full bg-gradient-to-r ${feature.color}`}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          viewport={{ once: true }}
          className="mt-10"
        >
          <div className="bg-gradient-to-r from-blue-50/50 to-emerald-50/50 rounded-2xl p-8 border border-blue-100/50">
            <div className="text-center mb-5">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Why Members Choose Us</h3>
              <p className="text-gray-600">Trusted by thousands of investors</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">{contentData?.why_choose_num1}</div>
                <div className="text-gray-700">{contentData?.why_choose_text1}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600 mb-2">{contentData?.why_choose_num2}</div>
                <div className="text-gray-700">{contentData?.why_choose_text2}</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-violet-600 mb-2">{contentData?.why_choose_num3}</div>
                <div className="text-gray-700">{contentData?.why_choose_text3}</div>
              </div>
            </div>
          </div>
        </motion.div>

  
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent"></div>
    </section>
  );
};

export default AboutPortal;